import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import type { AppDispatch } from "../../../core/data/redux/store";
import type { Job } from "../../../core/data/redux/recruitment/types";
import {
  selectJobs,
  selectJobsLoading,
  selectJobFilters,
  selectJobsPagination,
} from "../../../core/data/redux/recruitment/selectors";
import { setJobFilters } from "../../../core/data/redux/recruitment/slice";
import {
  fetchJobs,
  fetchJobDetails,
  deleteJob,
  publishJob,
  syncExternalJobs,
  closeJob,
  unpublishJob,
  updateJobStatus,
} from "../../../core/data/redux/recruitment/thunks";

import {
  loadDepartments,
  selectDepartmentOptions,
} from "../../../core/data/redux/dashboardSlice";

import JobFilters from "./components/JobFilters";
import JobCard from "./components/JobCard";
import JobTable from "./components/JobTable";
import JobFormModal from "./components/JobFormModal";
import PublishModal from "./components/PublishModal";
import CloseJobModal from "./components/CloseJobModal";
import CustomPagination from "./components/CustomPagination";
import RecruitmentHeader from "../components/RecruitmentHeader";
import { notify } from "../../../services/toast.config";

const JobsManagement: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const jobs = useSelector(selectJobs);
  const loading = useSelector(selectJobsLoading);
  const filters = useSelector(selectJobFilters);
  const pagination = useSelector(selectJobsPagination);
  const activeCompany = useSelector(
    (state: any) => state.recruitment.activeCompany,
  );
  const departmentOptions = useSelector(selectDepartmentOptions);

  const departmentMap = useMemo(() => {
    return (departmentOptions as any[]).reduce(
      (acc: Record<number, string>, d: any) => {
        acc[d.value] = d.label;
        return acc;
      },
      {},
    );
  }, [departmentOptions]);

  const [viewMode, setViewMode] = useState<"grid" | "list">("list");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit" | "clone">(
    "create",
  );
  const [modalJob, setModalJob] = useState<Job | null>(null);
  const [modalLoading, setModalLoading] = useState(false);

  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [publishModalJob, setPublishModalJob] = useState<Job | null>(null);
  const [publishLoading, setPublishLoading] = useState(false);

  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [closeModalJob, setCloseModalJob] = useState<Job | null>(null);
  const [closeLoading, setCloseLoading] = useState(false);

  const totalCount =
    (pagination as any)?.total_jobs ?? pagination?.total ?? jobs.length;

  useEffect(() => {
    if (!activeCompany?.company_id) return;
    dispatch(
      fetchJobs({
        search: filters.search || undefined,
        department_id: filters.department_id || undefined,
        employment_type: filters.employment_type || undefined,
        status: filters.status || undefined,
        page: filters.page,
        limit: filters.limit,
        date_from: filters.date_from,
        date_to: filters.date_to,
      }),
    );
  }, [
    dispatch,
    filters,
    activeCompany?.company_id,
    activeCompany?.company_type,
  ]);

  useEffect(() => {
    if (!activeCompany?.company_id) return;
    dispatch(
      loadDepartments({
        companyInfo: {
          company_id: activeCompany.company_id,
          company_name: activeCompany.company_name,
          company_type: activeCompany.company_type,
        },
      }),
    );
  }, [dispatch, activeCompany?.company_id]);

  const handleFilterChange = useCallback(
    (partial: Record<string, any>) => {
      dispatch(setJobFilters(partial));
    },
    [dispatch],
  );

  const openCreate = useCallback(() => {
    setModalMode("create");
    setModalJob(null);
    setModalLoading(false);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback(
    (job: Job) => {
      setModalMode("edit");
      setModalLoading(true);
      setModalOpen(true);

      dispatch(fetchJobDetails(job.id))
        .unwrap()
        .then((data: any) => setModalJob(data))
        .catch((err: any) => {
          notify.error(err || "Failed to load job details");
          setModalOpen(false);
        })
        .finally(() => setModalLoading(false));
    },
    [dispatch],
  );

  const openClone = useCallback(
    (job: Job) => {
      setModalMode("clone");
      setModalLoading(true);
      setModalOpen(true);
      dispatch(fetchJobDetails(job.id))
        .unwrap()
        .then((data: any) => setModalJob(data))
        .catch((err: any) => {
          notify.error(
            err || err?.data?.message || "Failed to load job for cloning",
          );
          setModalOpen(false);
        })
        .finally(() => setModalLoading(false));
    },
    [dispatch],
  );

  const openPublish = useCallback(
    (jobId: number) => {
      const job = jobs.find((j: any) => j.id === jobId);
      if (job) {
        setPublishModalJob(job);
        setPublishModalOpen(true);
      }
    },
    [jobs],
  );

  const openCloseJob = useCallback((job: Job) => {
    setCloseModalJob(job);
    setCloseModalOpen(true);
  }, []);

  const handleModalSuccess = useCallback(() => {
    setModalOpen(false);
    setModalJob(null);

    dispatch(
      fetchJobs({
        search: filters.search || undefined,
        department_id: filters.department_id || undefined,
        employment_type: filters.employment_type || undefined,
        status: filters.status || undefined,
        page: filters.page,
        limit: filters.limit,
      }),
    );
  }, [dispatch, filters]);

  const handleDelete = useCallback(
    async (jobId: number) => {
      try {
        await dispatch(deleteJob(jobId)).unwrap();
        notify.success("Job deleted successfully");
      } catch (err: any) {
        notify.error(err || err?.data?.message || "Failed to delete job");
      }
    },
    [dispatch],
  );

  const handlePublishSubmit = useCallback(
    async (jobId: number, platforms: string[]) => {
      setPublishLoading(true);
      try {
        await dispatch(publishJob({ jobId, platforms })).unwrap();
        notify.success(`Published to ${platforms.length} platform(s)`);
      } catch (err: any) {
        notify.error(err || err?.data?.message || "Failed to publish job");
        throw err;
      } finally {
        setPublishLoading(false);
      }
    },
    [dispatch],
  );

  const handleUnpublishFromModal = useCallback(
    async (jobId: number, platform: string) => {
      try {
        await dispatch(
          unpublishJob({ jobId, platformName: platform }),
        ).unwrap();
        notify.success(`Unpublished from ${platform}`);
      } catch (err: any) {
        notify.error(
          err ||
            err?.data?.error?.message ||
            err?.data.message ||
            `Failed to unpublish from ${platform}`,
        );
        throw err;
      }
    },
    [dispatch],
  );

  const handlePublishModalClose = useCallback(() => {
    setPublishModalOpen(false);
    setPublishModalJob(null);
    dispatch(fetchJobs({ page: filters.page, limit: filters.limit }));
  }, [dispatch, filters]);

  const handleCloseSubmit = useCallback(
    async (jobId: number, reason: string, closeOnExternal: boolean) => {
      setCloseLoading(true);
      try {
        await dispatch(
          closeJob({
            jobId,
            reason,
            closeOnExternalPlatforms: closeOnExternal,
          }),
        ).unwrap();
        notify.success("Job closed successfully");
        setCloseModalOpen(false);
        dispatch(fetchJobs({ page: filters.page, limit: filters.limit }));
      } catch (err: any) {
        notify.error(err || err?.data?.message || "Failed to close job");
      } finally {
        setCloseLoading(false);
      }
    },
    [dispatch, filters],
  );

  const handleStatusChange = useCallback(
    async (jobId: number, status: string) => {
      try {
        await dispatch(updateJobStatus({ jobId, status })).unwrap();
        notify.success(`Job status changed to ${status}`);
        dispatch(fetchJobs({ page: filters.page, limit: filters.limit }));
      } catch (err: any) {
        notify.error(err || err?.data?.message || "Failed to update status");
      }
    },
    [dispatch, filters],
  );

  const handleUnpublish = useCallback(
    async (jobId: number, platform: string) => {
      try {
        await dispatch(
          unpublishJob({ jobId, platformName: platform }),
        ).unwrap();
        notify.success(`Successfully unpublished from ${platform}`);
        dispatch(fetchJobs({ page: filters.page, limit: filters.limit }));
      } catch (err: any) {
        notify.error(
          err || err?.data?.message || `Failed to unpublish from ${platform}`,
        );
      }
    },
    [dispatch, filters],
  );

  const handleSync = useCallback(async () => {
    try {
      await dispatch(syncExternalJobs()).unwrap();
      notify.success("External jobs synced successfully");
      dispatch(fetchJobs({ page: 1, limit: filters.limit }));
    } catch (err: any) {
      notify.error(err || err?.data?.message || "Sync failed.");
    }
  }, [dispatch, filters]);

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <RecruitmentHeader title="Jobs Management" breadcrumb="Jobs">
            <button
              className="btn btn-success d-flex align-items-center mb-2"
              onClick={handleSync}>
              <i className="ti ti-refresh me-2" />
              Sync External
            </button>
            <button
              className="btn btn-info d-flex align-items-center mb-2"
              onClick={openCreate}>
              <i className="ti ti-plus me-2" />
              Create Job
            </button>
          </RecruitmentHeader>

          <JobFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            totalCount={totalCount}
            isLoading={loading}
            departmentOptions={departmentOptions}
          />

          {viewMode === "list" ? (
            <JobTable
              jobs={jobs}
              loading={loading}
              filters={filters}
              totalCount={totalCount}
              onFilterChange={handleFilterChange}
              onEdit={openEdit}
              onDelete={handleDelete}
              onPublish={openPublish}
              onClone={openClone}
              onChangeStatus={handleStatusChange}
              onCloseJob={openCloseJob}
              onUnpublish={handleUnpublish}
              departmentMap={departmentMap}
            />
          ) : loading ? (
            <div
              className="d-flex justify-content-center align-items-center bg-white"
              style={{ minHeight: 400, borderRadius: 12 }}>
              <div className="text-center">
                <div
                  className="spinner-border text-primary"
                  role="status"
                  style={{ width: "3rem", height: "3rem" }}
                />
                <p className="mt-3 text-muted">Loading jobs...</p>
              </div>
            </div>
          ) : (
            <div
              className="bg-white p-4"
              style={{ borderRadius: 12, border: "1px solid #e2e8f0" }}>
              <div className="row">
                {jobs.length > 0 ? (
                  jobs.map((job: Job) => (
                    <div
                      key={job.id}
                      className="col-xxl-3 col-xl-4 col-md-6 mb-4">
                      <JobCard
                        job={job}
                        onEdit={openEdit}
                        onDelete={handleDelete}
                        onPublish={openPublish}
                        onClone={openClone}
                        onChangeStatus={handleStatusChange}
                        onCloseJob={openCloseJob}
                        onUnpublish={handleUnpublish}
                        departmentMap={departmentMap}
                      />
                    </div>
                  ))
                ) : (
                  <div className="col-12">
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ minHeight: 300 }}>
                      <div className="text-center">
                        <i
                          className="ti ti-file-search text-muted"
                          style={{ fontSize: "4rem" }}
                        />
                        <h5 className="mt-3 mb-2 fw-semibold">No Jobs Found</h5>
                        <p className="text-muted mb-0">
                          Try adjusting your filters or create a new job.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {jobs.length > 0 && (
                <div className="mt-2 pt-2 border-top">
                  <CustomPagination
                    currentPage={filters.page || 1}
                    totalPages={Math.ceil(totalCount / (filters.limit || 10))}
                    total={totalCount}
                    limit={filters.limit || 10}
                    hasNextPage={
                      (filters.page || 1) * (filters.limit || 10) < totalCount
                    }
                    hasPrevPage={(filters.page || 1) > 1}
                    onPageChange={(page) => handleFilterChange({ page })}
                    onLimitChange={(limit) =>
                      handleFilterChange({ limit, page: 1 })
                    }
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © CMS.</p>
          <p>
            Designed & Developed By{" "}
            <Link
              to="#"
              className="text-primary"
              onClick={(e: any) => e.preventDefault()}>
              ZYVO
            </Link>
          </p>
        </div>
      </div>

      <JobFormModal
        open={modalOpen}
        mode={modalMode}
        job={modalJob}
        loading={modalLoading}
        onCancel={() => {
          setModalOpen(false);
          setModalJob(null);
        }}
        onSuccess={handleModalSuccess}
        departmentOptions={departmentOptions}
      />

      <PublishModal
        open={publishModalOpen}
        job={publishModalJob}
        loading={publishLoading}
        onCancel={handlePublishModalClose}
        onPublish={handlePublishSubmit}
        onUnpublish={handleUnpublishFromModal}
      />

      <CloseJobModal
        open={closeModalOpen}
        job={closeModalJob}
        loading={closeLoading}
        onCancel={() => {
          setCloseModalOpen(false);
          setCloseModalJob(null);
        }}
        onCloseJob={handleCloseSubmit}
      />
    </>
  );
};

export default JobsManagement;
